import { useState, useEffect, useMemo } from "react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  recipes,
  ingredientFoodMap,
  extractGrams,
  getAllFoodIds,
  type MealKey,
  type Ingredient,
  type Recipe,
  type MealBlock,
} from "@/data/recipes";
import { mealOrder } from "@/data/recipes";

interface FoodNutrition {
  energy_kcal: number;
  protein_g: number;
  carbohydrate_g: number;
  lipid_g: number;
  fiber_g: number;
  source: string;
}

type FoodMap = Record<number, FoodNutrition>;

export function useDietRecipes() {
  const { user } = useAuth();
  const [foodData, setFoodData] = useState<FoodMap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = getAllFoodIds();
    if (ids.length === 0) {
      setLoading(false);
      return;
    }
    // Anonymous preview — use hardcoded recipe data, skip authenticated fetch
    if (!user) {
      setLoading(false);
      return;
    }

    apiFetch<{ data: FoodMap }>("api-diet-recipes", { ids: ids.join(",") })
      .then((res) => setFoodData(res.data))
      .catch((err) => console.error("Failed to fetch diet recipe nutrition:", err))
      .finally(() => setLoading(false));
  }, [user]);

  const enrichedRecipes = useMemo(() => {
    if (!foodData) return recipes;

    const result: Record<MealKey, MealBlock> = {} as any;

    for (const key of mealOrder) {
      const block = recipes[key];
      result[key] = {
        ...block,
        items: block.items.map((recipe) => enrichRecipe(recipe, foodData)),
      };
    }

    return result;
  }, [foodData]);

  return { enrichedRecipes, loading };
}

function enrichRecipe(recipe: Recipe, foodData: FoodMap): Recipe {
  const enrichedIngredients = recipe.ingredients.map((ing) =>
    enrichIngredient(ing, foodData)
  );

  // Recalculate recipe totals from enriched ingredients
  const totalCal = Math.round(
    enrichedIngredients.reduce((sum, i) => sum + i.cal, 0)
  );
  const totalProtein = Math.round(
    enrichedIngredients.reduce((sum, i) => sum + i.protein, 0) * 10
  ) / 10;

  return {
    ...recipe,
    cal: totalCal,
    protein: totalProtein,
    ingredients: enrichedIngredients,
  };
}

function enrichIngredient(ing: Ingredient, foodData: FoodMap): Ingredient {
  const mapping = ingredientFoodMap[ing.name];
  if (!mapping) {
    // No mapping → keep hardcoded, mark as ESTIMADO if it has calories
    if (ing.cal > 0) {
      return { ...ing, source: "ESTIMADO" };
    }
    return ing;
  }

  const nutrition = foodData[mapping.foodId];
  if (!nutrition) return ing; // Not found in DB → keep hardcoded

  // Skip if DB has 0 energy (data issue)
  if (nutrition.energy_kcal === 0) return ing;

  const grams = extractGrams(ing.amount, ing.name) ?? mapping.defaultGrams;
  if (!grams) return ing; // Can't determine quantity → keep hardcoded

  // Values in DB are per 100g
  const factor = grams / 100;

  return {
    ...ing,
    taco_food_id: mapping.foodId,
    quantity_grams: grams,
    source: nutrition.source,
    cal: Math.round(nutrition.energy_kcal * factor),
    protein: Math.round(nutrition.protein_g * factor * 10) / 10,
    carbs: Math.round(nutrition.carbohydrate_g * factor * 10) / 10,
    fat: Math.round(nutrition.lipid_g * factor * 10) / 10,
  };
}
